import { createApi } from '@reduxjs/toolkit/query/react';
import { baseWalletQuery } from './config';
import {
  SubscriptionInfo,
  SubscriptionIntent,
  CreateSubscriptionRequest,
  SubscriptionPlanInfo,
  UpgradeDowngradeSubscriptionRequest,
  GetSubscriptionPlansRequest,
  CreatePaypalSubscriptionRequest,
  GetSubscriptionPlanRequest,
  CancellationReason,
  CreditPacksOfferedResponse,
} from 'src/types';
import { env } from 'src/env';
import { teamsApi, TeamsTags } from 'src/store/services/teamsApi';
import { SetupIntentResponse } from 'src/types/wallet/models/SetupIntentResponse';
import { CreditAccountInfo } from 'src/types/wallet/models/CreditAccountInfo';
import { CreateAccountRequest } from 'src/types/wallet/models/CreateAccountRequest';
import { UpdateAccountRequest } from 'src/types/wallet/models/UpdateAccountRequest';
import { CreditAccountTransactionsResponse } from 'src/types/wallet/models/CreditAccountTransactionsResponse';
import { UserEligibleOffers } from 'src/types/wallet/models/UserEligibleOffers';
import { UserReferralDetails } from 'src/types/wallet/models/UserReferralDetails';
import { CancelOffersResponse } from 'src/types/wallet/models/CancelOffersResponse';

// import log from 'src/utils/logger';

export enum WalletTags {
  Token = 'Token',
  Costs = 'Costs',
  UserSubscriptionInfo = 'UserSubscriptionInfo',
  BudgetQuota = 'BudgetQuota',
  TasksQuota = 'TasksQuota',
  TasksCosts = 'TasksCosts',
  SessionVerification = 'SessionVerification',
  Plans = 'Plans',
  PlanById = 'PlanById',
  CreditsInfo = 'CreditsInfo',
  APIUsages = 'APIUsages',
  OfferByUserId = 'OfferByUserId',
  ReferralByUserId = 'ReferralByUserId',
  CreditPacks = 'CreditPacks',
}

export interface UpgradeSubscriptionArgs {
  user_id: string;
  lookup_key: string;
  promotion_code?: string;
  embedded?: boolean;
  add_on_amount_usd_cents?: 500 | 1000 | 1500;
}

export interface UpgradeSubscriptionResult {
  checkout_session?: {
    url: string;
    client_secret: string;
  };
  billing_portal_session?: {
    url: string;
  };
}

export const walletApi = createApi({
  reducerPath: 'walletApi',
  tagTypes: [
    WalletTags.Token,
    WalletTags.Costs,
    WalletTags.UserSubscriptionInfo,
    WalletTags.BudgetQuota,
    WalletTags.TasksQuota,
    WalletTags.TasksCosts,
    WalletTags.SessionVerification,
    WalletTags.Plans,
    WalletTags.PlanById,
    WalletTags.CreditsInfo,
    WalletTags.APIUsages,
    WalletTags.OfferByUserId,
    WalletTags.ReferralByUserId,
    WalletTags.CreditPacks,
  ],
  refetchOnMountOrArgChange: true, // till not implemented PDU
  baseQuery: baseWalletQuery,
  endpoints: (builder) => ({
    // TODO(olha): add types from fast api when it will be ready
    getStripePrices: builder.query<unknown, { user_id: string }>({
      query: ({ user_id }) => `/stripe/prices/user/${user_id}`,
      providesTags: () => [WalletTags.Token],
      keepUnusedDataFor: 60 * 60 * 10,
    }),
    // TODO(olha): add types from fast api when it will be ready
    getSessionInfo: builder.query<
      unknown,
      { user_id: string; session_id: string }
    >({
      query: ({ user_id, session_id }) =>
        `/stripe/user/${user_id}/session-id/${session_id}`,
      providesTags: () => [WalletTags.SessionVerification],
    }),
    // TODO: add types from fast api when it is ready
    getStripeCosts: builder.query<
      { charged_tasks: never[]; next_evaluated_key: string },
      { user_id: string; next_evaluated_key: string }
    >({
      query: ({ user_id, next_evaluated_key }) =>
        `/stripe/activity/user/${user_id}${next_evaluated_key ? '?next_evaluated_key=' + next_evaluated_key : ''}`,
      providesTags: () => [WalletTags.Costs],
    }),
    // TODO: add types from fast api when it is ready
    getUserSubscriptionInfo: builder.query<
      SubscriptionInfo,
      { user_id: string }
    >({
      query: ({ user_id }) => `/subscriptions/user/${user_id}`,
      providesTags: () => [WalletTags.UserSubscriptionInfo],
    }),
    // TODO: add types from fast api when it is ready
    getUserBudgetQuotaInfo: builder.query<
      {
        amount: number;
        is_low_balance: boolean;
        quota_exceeded: boolean;
      },
      { user_id: string }
    >({
      query: ({ user_id }) => `/stripe/budget-quota/user/${user_id}`,
      providesTags: () => [WalletTags.BudgetQuota],
    }),
    // TODO: add types from fast api when it is ready
    getUserTaskQuotaInfo: builder.query<
      {
        count: number;
      },
      { user_id: string }
    >({
      query: ({ user_id }) => `/stripe/task-count-quota/user/${user_id}`,
      providesTags: () => [WalletTags.TasksQuota],
      keepUnusedDataFor: 60 * 60 * 10,
    }),
    // TODO(olha): add types from fast api when it will be ready
    upgradeSubscription: builder.mutation<
      UpgradeSubscriptionResult,
      UpgradeSubscriptionArgs
    >({
      query: ({
        user_id,
        lookup_key,
        promotion_code,
        embedded,
        add_on_amount_usd_cents,
      }) => ({
        url: `stripe/checkout-session/user/${user_id}`,
        method: 'POST',
        params: {
          lookup_key,
          promotion_code,
          embedded,
          add_on_amount_usd_cents,
        },
        body: {
          success_url: env.REACT_APP_NINJA_UI_URL,
          cancel_url: env.REACT_APP_NINJA_UI_URL,
        },
      }),
    }),
    renewSubscription: builder.mutation({
      query: ({ user_id, lookup_key }) => ({
        url: `stripe/subscription/renew/early/user/${user_id}?lookup_key=${lookup_key}`,
        method: 'POST',
      }),
    }),
    // TODO(olha): add types from fast api when it will be ready
    getBillingPortalUrl: builder.query<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      any,
      { user_id: string; return_url?: string }
    >({
      query: ({ user_id, return_url }) => ({
        url: `/stripe/billing-portal/user/${user_id}`,
        params: {
          // TODO(olha): check why we have / in the UI_URL at the end
          return_url: `${env.REACT_APP_NINJA_UI_URL}${return_url}`,
        },
      }),
    }),
    getUserTaskCostsInfo: builder.query<
      {
        chargeable_costs: {
          item: {
            name: string;
          };
          cost: {
            amount: number;
            currency: string;
          };
        }[];
      },
      { user_id: string; task_id: string }
    >({
      query: ({ user_id, task_id }) =>
        `/stripe/costs/user/${user_id}/task/${task_id}`,
      providesTags: () => [WalletTags.TasksCosts],
    }),
    createSubscription: builder.mutation<
      SubscriptionIntent,
      CreateSubscriptionRequest
    >({
      query: ({
        user_id,
        plan_id,
        seat_count,
        stripe_promotion_code,
        referral_id,
        discount_type,
      }) => ({
        url: `stripe/subscription/user/${user_id}`,
        params: {
          plan_id,
          ...(!!seat_count && {
            seat_count,
          }),
          ...(!!stripe_promotion_code && {
            stripe_promotion_code,
          }),
          ...(!!referral_id && {
            referral_id,
          }),
          ...(!!discount_type && {
            discount_type,
          }),
        },
        method: 'PUT',
      }),
    }),
    updateDowngradeSubscription: builder.mutation<
      {
        status: string;
        approve_url?: string;
      },
      UpgradeDowngradeSubscriptionRequest
    >({
      query: ({ user_id, product_key, price_timestamp }) => ({
        url: `subscriptions/user/${user_id}/upgrade_or_downgrade?upgrade_product_key=${product_key}&price_timestamp=${price_timestamp}`,
        method: 'PUT',
      }),
    }),
    changePlan: builder.mutation<
      {
        status: string;
        approve_url?: string;
      },
      UpgradeDowngradeSubscriptionRequest
    >({
      query: ({ user_id, product_key, price_timestamp, seat_count }) => ({
        url: `subscriptions/user/${user_id}/change_plan?product_key=${product_key}&price_timestamp=${price_timestamp}${seat_count ? `&seat_count=${seat_count}` : ''}`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, arg) => {
        if (!!result && !result.approve_url) {
          return [WalletTags.UserSubscriptionInfo];
        }
        return [];
      },
      async onQueryStarted({ ...patch }, { dispatch, queryFulfilled }) {
        if (patch.seat_count) {
          try {
            await queryFulfilled;
            dispatch(teamsApi.util.invalidateTags([TeamsTags.TeamByUserId]));
          } catch {}
        }
      },
    }),
    getSubscriptionPlans: builder.query<
      SubscriptionPlanInfo[],
      GetSubscriptionPlansRequest
    >({
      query: ({
        user_id,
        group,
        seat_count,
        referral_id,
        families,
        discount_type,
      }) => ({
        url: '/plans/',
        params: {
          user_id,
          group: group,
          seat_count: seat_count,
          referral_id,
          families,
          discount_type,
        },
      }),
      providesTags: () => [WalletTags.Plans],
      keepUnusedDataFor: 60 * 60 * 10,
    }),
    getSubscriptionPlanById: builder.query<
      SubscriptionPlanInfo,
      GetSubscriptionPlanRequest
    >({
      query: ({
        user_id,
        plan_id,
        seat_count,
        stripe_promotion_code,
        referral_id,
        discount_type,
      }) => ({
        url: `/plans/kinds/${plan_id}`,
        params: {
          user_id,
          seat_count,
          stripe_promotion_code,
          referral_id,
          discount_type,
        },
      }),
      providesTags: () => [WalletTags.PlanById],
      keepUnusedDataFor: 60 * 60 * 10,
    }),
    createPaypalSubscription: builder.mutation<
      string,
      CreatePaypalSubscriptionRequest
    >({
      query: ({ user_id, subscription_id }) => ({
        url: `paypal/subscription/${subscription_id}/user/${user_id}`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, arg) => [
        WalletTags.UserSubscriptionInfo,
      ],
    }),
    cancelSubscription: builder.mutation<string, string>({
      query: (user_id) => ({
        url: `/subscriptions/user/${user_id}/cancel`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, arg) => [
        WalletTags.UserSubscriptionInfo,
      ],
    }),
    resumeSubscription: builder.mutation<string, string>({
      query: (user_id) => ({
        url: `/subscriptions/user/${user_id}/resume`,
        method: 'PUT',
      }),
      invalidatesTags: (result, error, arg) => [
        WalletTags.UserSubscriptionInfo,
      ],
    }),
    updateCancelSubscriptionReason: builder.mutation<
      string,
      {
        user_id: string;
        reason: CancellationReason;
      }
    >({
      query: ({ user_id, reason }) => ({
        url: `/subscriptions/user/${user_id}/record_cancellation_reason`,
        method: 'PUT',
        body: {
          reason,
        },
      }),
      invalidatesTags: (result, error, arg) => [
        WalletTags.UserSubscriptionInfo,
      ],
    }),
    setupIntent: builder.mutation<SetupIntentResponse, { user_id: string }>({
      query: ({ user_id }) => ({
        url: `stripe/users/${user_id}/setup-intent`,
        method: 'POST',
      }),
    }),
    getCreditAccountInfo: builder.query<CreditAccountInfo, { user_id: string }>(
      {
        query: ({ user_id }) => ({
          url: `/credit_accounts/users/${user_id}`,
        }),
        providesTags: () => [WalletTags.CreditsInfo],
      },
    ),
    createCreditAccount: builder.mutation<
      CreditAccountInfo,
      { user_id: string; paymentInfo: CreateAccountRequest }
    >({
      query: ({ user_id, paymentInfo }) => ({
        url: `credit_accounts/users/${user_id}/accounts`,
        method: 'POST',
        body: {
          ...paymentInfo,
        },
      }),
      invalidatesTags: () => [WalletTags.CreditsInfo],
    }),
    updateCreditAccount: builder.mutation<
      CreditAccountInfo,
      {
        user_id: string;
        paymentInfo: UpdateAccountRequest;
        credit_account: string;
      }
    >({
      query: ({ user_id, paymentInfo, credit_account }) => ({
        url: `credit_accounts/users/${user_id}/accounts/${credit_account}`,
        method: 'PUT',
        body: {
          ...paymentInfo,
        },
      }),
      invalidatesTags: () => [WalletTags.CreditsInfo],
    }),
    getAPIUsages: builder.query<
      CreditAccountTransactionsResponse,
      {
        user_id: string;
        account_id: string;
        next_page_token?: string;
        previous_page_token?: string;
      }
    >({
      query: ({
        user_id,
        account_id,
        next_page_token,
        previous_page_token,
      }) => ({
        url: `/credit_accounts/users/${user_id}/accounts/${account_id}/transactions`,
        params: {
          next_page_token: next_page_token || undefined,
          previous_page_token: previous_page_token || undefined,
        },
      }),
      keepUnusedDataFor: 0,
      providesTags: () => [WalletTags.APIUsages],
    }),
    getOfferInfoUserId: builder.query<UserEligibleOffers, { user_id: string }>({
      query: ({ user_id }) => ({
        url: `/offers/user/${user_id}`,
      }),
      providesTags: () => [WalletTags.OfferByUserId],
      keepUnusedDataFor: 60 * 60 * 10,
    }),
    claimOffer: builder.mutation({
      query: ({ user_id, offer_type }) => ({
        url: `/offers/user/${user_id}/claim`,
        method: 'POST',
        body: {
          offer_type,
        },
      }),
    }),
    getReferralInfoUserId: builder.query<
      UserReferralDetails,
      { user_id: string }
    >({
      query: ({ user_id }) => ({
        url: `/offers/user/${user_id}/referral`,
      }),
      providesTags: () => [WalletTags.ReferralByUserId],
      keepUnusedDataFor: 60 * 60 * 10,
    }),
    getComplementaryOffer: builder.query<
      CancelOffersResponse,
      { user_id: string }
    >({
      query: ({ user_id }) => ({
        url: `/offers/user/${user_id}/cancel_offers`,
      }),
      keepUnusedDataFor: 60 * 60 * 10,
    }),
    getCreditPack: builder.query<
      CreditPacksOfferedResponse,
      { user_id: string }
    >({
      query: ({ user_id }) => ({
        url: `/credit_accounts/credit_packs?user_id=${user_id}`,
      }),
      keepUnusedDataFor: 60 * 60 * 10,
      providesTags: () => [WalletTags.CreditPacks],
    }),
  }),
});

export const {
  useGetUserBudgetQuotaInfoQuery,
  useGetUserTaskQuotaInfoQuery,
  useLazyGetUserTaskQuotaInfoQuery,
  useGetUserSubscriptionInfoQuery,
  useGetStripeCostsQuery,
  useGetStripePricesQuery,
  useUpgradeSubscriptionMutation,
  useLazyGetBillingPortalUrlQuery,
  useRenewSubscriptionMutation,
  useLazyGetUserBudgetQuotaInfoQuery,
  useGetUserTaskCostsInfoQuery,
  useLazyGetSessionInfoQuery,
  useCreateSubscriptionMutation,
  useGetSubscriptionPlansQuery,
  useUpdateDowngradeSubscriptionMutation,
  useCreatePaypalSubscriptionMutation,
  useCancelSubscriptionMutation,
  useResumeSubscriptionMutation,
  useChangePlanMutation,
  useGetSubscriptionPlanByIdQuery,
  useUpdateCancelSubscriptionReasonMutation,
  useSetupIntentMutation,
  useCreateCreditAccountMutation,
  useGetCreditAccountInfoQuery,
  useUpdateCreditAccountMutation,
  useGetAPIUsagesQuery,
  useGetOfferInfoUserIdQuery,
  useClaimOfferMutation,
  useGetReferralInfoUserIdQuery,
  useGetComplementaryOfferQuery,
  useGetCreditPackQuery,
} = walletApi;
